/**
 * The product's menu, in one place.
 *
 * The sidebar, the home launcher and Ctrl+K search all read this list, so
 * a screen added here appears in all three, and the permission that hides
 * it is declared once. A user who cannot post journals never sees the
 * Journal entry anywhere -- showing a control that fails on submit wastes
 * the user's time and teaches them to distrust the screen.
 */
import { P, type Me } from '../api/types'

export type MenuIcon =
  | 'home' | 'contra' | 'payment' | 'receipt' | 'journal' | 'sales' | 'purchase'
  | 'daybook' | 'outstanding' | 'ledger' | 'trial' | 'balance' | 'pnl'
  | 'register' | 'cash' | 'bank' | 'vat' | 'accounts' | 'settings'
  | 'trip' | 'invoice' | 'fleet' | 'tripRegister' | 'profit'
  | 'items' | 'stores' | 'openingStock' | 'purchases' | 'invSales'
  | 'stockMoves' | 'stockOnHand' | 'stockCard' | 'vanStock' | 'salesByItem' | 'lowStock'
  | 'vanSales' | 'vanPhones'

export interface MenuItem {
  /** i18n key of the label. */
  key: string
  path: string
  icon: MenuIcon
  /** Permission needed to see it; null = everyone signed in. */
  perm: string | null
  /** Keyboard shortcut, where one exists (registered on the home screen). */
  combo?: string
  /** Voucher kind, for the voucher entries. */
  kind?: string
  /** Company module the item belongs to; hidden where it is switched off. */
  module?: ModuleName
}

/** Modules a company switches on (Settings → Modules). */
export type ModuleName = 'transport' | 'inventory'

export interface MenuGroup {
  /** i18n key of the group heading. */
  key: string
  id: 'overview' | 'transactions' | 'reports' | 'inventory' | 'transport' | 'masters'
  items: MenuItem[]
}

export const TXN: MenuItem[] = [
  { key: 'voucher.contra', kind: 'contra', path: '/vouchers/contra', icon: 'contra',
    combo: 'alt+4', perm: P.postContra },
  { key: 'voucher.payment', kind: 'payment', path: '/vouchers/payment', icon: 'payment',
    combo: 'alt+5', perm: P.postPayment },
  { key: 'voucher.receipt', kind: 'receipt', path: '/vouchers/receipt', icon: 'receipt',
    combo: 'alt+6', perm: P.postReceipt },
  { key: 'voucher.journal', kind: 'journal', path: '/vouchers/journal', icon: 'journal',
    combo: 'alt+7', perm: P.postJournal },
  { key: 'voucher.sales', kind: 'sales', path: '/vouchers/sales', icon: 'sales',
    combo: 'alt+8', perm: P.postSales },
  { key: 'voucher.purchase', kind: 'purchase', path: '/vouchers/purchase', icon: 'purchase',
    combo: 'alt+9', perm: P.postPurchase },
]

export const REPORTS: MenuItem[] = [
  { key: 'nav.dayBook', path: '/daybook', icon: 'daybook', combo: 'alt+d',
    perm: P.reportOperational },
  { key: 'nav.outstanding', path: '/outstanding', icon: 'outstanding', combo: 'alt+o',
    perm: P.reportOperational },
  { key: 'nav.ledger', path: '/ledger', icon: 'ledger', combo: 'alt+l',
    perm: P.reportOperational },
  { key: 'nav.trialBalance', path: '/trial-balance', icon: 'trial', combo: 'alt+t',
    perm: P.reportFinancial },
  { key: 'nav.balanceSheet', path: '/balance-sheet', icon: 'balance', combo: 'alt+b',
    perm: P.reportFinancial },
  // Alt+P: the owner's first question deserves a key of its own.
  { key: 'nav.profitLoss', path: '/profit-loss', icon: 'pnl', combo: 'alt+p',
    perm: P.reportFinancial },
  // Operational, so the clerk who raises the invoices can read them back.
  { key: 'nav.salesRegister', path: '/sales-register', icon: 'register', combo: 'alt+s',
    perm: P.reportOperational },
  { key: 'nav.purchaseRegister', path: '/purchase-register', icon: 'register',
    combo: 'alt+u', perm: P.reportOperational },
  { key: 'nav.cashBook', path: '/cash-book', icon: 'cash', combo: 'alt+k',
    perm: P.reportOperational },
  { key: 'nav.bankBook', path: '/bank-book', icon: 'bank', combo: 'alt+j',
    perm: P.reportOperational },
  { key: 'nav.vatReturn', path: '/vat-return', icon: 'vat', combo: 'alt+x',
    perm: P.reportFinancial },
]

export const MASTERS: MenuItem[] = [
  { key: 'nav.chartOfAccounts', path: '/accounts', icon: 'accounts', combo: 'alt+m',
    perm: P.masterView },
  { key: 'nav.settings', path: '/settings', icon: 'settings', combo: 'alt+g',
    perm: P.masterView },
]

/**
 * Transport (BizTrans) is a module: its own group, shown only in a company
 * that has switched it on, and within that only to users with the rights.
 * A user with transport rights in HiFi sees none of it after switching to
 * a company that runs no trucks.
 */
export const TRANSPORT: MenuItem[] = [
  { key: 'nav.tripEntry', path: '/transport/trips', icon: 'trip', perm: P.transportEntry,
    module: 'transport' },
  { key: 'nav.tripInvoicing', path: '/transport/invoicing', icon: 'invoice',
    perm: P.transportInvoice, module: 'transport' },
  { key: 'nav.fleet', path: '/transport/fleet', icon: 'fleet', perm: P.transportView,
    module: 'transport' },
  { key: 'nav.tripRegister', path: '/transport/register', icon: 'tripRegister',
    perm: P.transportView, module: 'transport' },
  // A margin report: financial as well as transport.
  { key: 'nav.vehicleProfit', path: '/transport/profitability', icon: 'profit',
    perm: P.reportFinancial, module: 'transport' },
]

/**
 * Inventory & Billing is a module, like Transport: its group appears only
 * in a company that has switched it on.
 */
export const INVENTORY: MenuItem[] = [
  // The salesman's own screen: the phone app, offline.
  { key: 'nav.vanSales', path: '/van', icon: 'vanSales', perm: P.inventoryVan,
    module: 'inventory' },
  { key: 'nav.invSales', path: '/inventory/sales', icon: 'invSales',
    perm: P.postSales, module: 'inventory' },
  { key: 'nav.purchases', path: '/inventory/purchases', icon: 'purchases',
    perm: P.postPurchase, module: 'inventory' },
  { key: 'nav.items', path: '/inventory/items', icon: 'items', combo: 'alt+i',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.stores', path: '/inventory/stores', icon: 'stores',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.stockMoves', path: '/inventory/transfers', icon: 'stockMoves',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.stockOnHand', path: '/inventory/stock', icon: 'stockOnHand',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.stockCard', path: '/inventory/stock-card', icon: 'stockCard',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.vanStock', path: '/inventory/van-stock', icon: 'vanStock',
    perm: P.inventoryView, module: 'inventory' },
  // Operational, like the sales register; the cost columns are financial.
  { key: 'nav.salesByItem', path: '/inventory/sales-by-item', icon: 'salesByItem',
    perm: P.reportOperational, module: 'inventory' },
  { key: 'nav.lowStock', path: '/inventory/reorder', icon: 'lowStock',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.vanPhones', path: '/inventory/van-phones', icon: 'vanPhones',
    perm: P.inventoryView, module: 'inventory' },
  { key: 'nav.openingStock', path: '/inventory/opening', icon: 'openingStock',
    perm: P.inventoryMasters, module: 'inventory' },
]

export const MENU: MenuGroup[] = [
  { id: 'overview', key: 'shell.groupOverview',
    items: [{ key: 'nav.home', path: '/', icon: 'home', perm: null, combo: 'alt+h' }] },
  { id: 'transactions', key: 'home.transactions', items: TXN },
  { id: 'reports', key: 'shell.groupReports', items: REPORTS },
  { id: 'inventory', key: 'shell.groupInventory', items: INVENTORY },
  { id: 'transport', key: 'home.transport', items: TRANSPORT },
  { id: 'masters', key: 'shell.groupMasters', items: MASTERS },
]

/** The modules switched on for the company the user is working in. */
export function modulesOf(me: Pick<Me, 'company_id' | 'companies'> | null | undefined):
ModuleName[] {
  const c = me?.companies?.find((x) => x.id === me.company_id)
  return (c?.modules ?? []) as ModuleName[]
}

/** May this user see `item` in this company? Rights, and the module. */
export function isVisible(item: MenuItem, can: (perm: string) => boolean,
  modules: readonly string[]): boolean {
  return (!item.perm || can(item.perm)) && (!item.module || modules.includes(item.module))
}

/** The menu as this user may see it: forbidden items and empty groups removed. */
export function visibleMenu(can: (perm: string) => boolean,
  modules: readonly string[]): MenuGroup[] {
  return MENU
    .map((g) => ({ ...g, items: g.items.filter((i) => isVisible(i, can, modules)) }))
    .filter((g) => g.items.length > 0)
}

/** Is `item` the screen at `pathname`? Home matches only itself. */
export function isCurrent(item: MenuItem, pathname: string): boolean {
  if (item.path === '/') return pathname === '/'
  return pathname === item.path || pathname.startsWith(`${item.path}/`)
}
